import { Box, CircularProgress, Skeleton, Tooltip, Paper, Typography, Chip, Button } from "@mui/material";
import { GoogleMap, InfoWindow } from "@react-google-maps/api";
import { MarkerF, Marker, Polyline } from "../../../theme/maps";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTrackerDevices } from "../../../hooks";
import { useSocket } from "../../../context/SocketContext";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import { TimeLine } from "../../common/TimeLine";
import { DateSelector } from "../../common/DateSelector";
import dayjs from "dayjs";
import { IconButtonComponent } from "../../common/IconButtonComponent";
import { RecordInfo } from "../../DeviceTracker/RecordInfo";
import { colors } from "../../ui/colors";

export const WorkforceMap = ({ employee, device, currentLocation = null, mapHeight = "90%" }) => {
  const workforce = employee;
  const tracker = device || workforce?.deviceTracker;
  const { socket } = useSocket() || {};

  const mapContainerStyle = {
    padding: 2,
    width: "100%",
    height: mapHeight,
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(12, 12, 12, 0.08)",
  };

  const defaultCenter = {
    lat: 19.4326,
    lng: -99.1332,
  };

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [isMapReady, setIsMapReady] = useState(false);
  const [user, setUser] = useState(null);
  const [infoRecord, setInfoRecord] = useState(null);
  const mapRef = useRef(null);
  const [recordIndex, setRecordIndex] = useState(-1);
  const [autoFollow, setAutoFollow] = useState(true);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  const { trackerRecords, loading, addRecord } = useTrackerDevices(tracker?.uid, selectedDate);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMapReady(true);

      const resizeTimer = setTimeout(() => {
        if (mapRef.current && window.google && window.google.maps) {
          window.google.maps.event.trigger(mapRef.current, "resize");
        }
      }, 100);

      return () => clearTimeout(resizeTimer);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Sort records chronologically ascending (oldest → newest)
  // Filter out records with invalid createdAt and sort the rest
  const sortedRecords = useMemo(() => {
    return [...trackerRecords]
      .filter((rec) => {
        if (!rec?.createdAt) return false;
        const date = new Date(rec.createdAt);
        return !isNaN(date.getTime());
      })
      .sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateA.getTime() - dateB.getTime();
      });
  }, [trackerRecords]);

  const mapFocus = user || currentLocation;

  const pathCoords = useMemo(() => {
    return sortedRecords
      .slice()
      ?.sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt))
      .map((rec) => {
        const lat = parseFloat(rec?.gps?.latitude);
        const lng = parseFloat(rec?.gps?.longitude);
        if (isNaN(lat) || isNaN(lng)) return null;
        return { lat, lng };
      })
      ?.filter(Boolean);
  }, [sortedRecords]);

  // Update map center when records are available
  useEffect(() => {
    if (!mapRef.current || !isMapReady) return;
    const target = user;
    if (!target) return;
    setMapCenter(target);
    mapRef.current.panTo(target);
    mapRef.current.setZoom(16);
  }, [user, sortedRecords, isMapReady]);

  useEffect(() => {
    if (autoFollow && sortedRecords.length > 0) {
      setRecordIndex(sortedRecords.length - 1);
    }
  }, [sortedRecords.length, autoFollow]);

  useEffect(() => {
    if (recordIndex < 0 || recordIndex >= sortedRecords.length) return;
    const rec = sortedRecords[recordIndex];
    if (!rec?.gps) return;

    const lat = parseFloat(rec.gps.latitude);
    const lng = parseFloat(rec.gps.longitude);

    // Validate coordinates: not NaN, not null, not undefined, and not 0,0
    if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0 || lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return;

    if (mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      // Check if this is the last valid record by finding the last valid index
      let isLastValid = false;
      for (let i = sortedRecords.length - 1; i >= 0; i--) {
        const record = sortedRecords[i];
        if (!record?.gps) continue;
        const recordLat = parseFloat(record.gps.latitude);
        const recordLng = parseFloat(record.gps.longitude);
        if (
          !isNaN(recordLat) &&
          !isNaN(recordLng) &&
          recordLat !== 0 &&
          recordLng !== 0 &&
          recordLat !== null &&
          recordLng !== null &&
          Math.abs(recordLat) <= 90 &&
          Math.abs(recordLng) <= 180
        ) {
          isLastValid = i === recordIndex;
          break;
        }
      }
      if (isLastValid) {
        mapRef.current.setZoom(19);
      }
    }
  }, [recordIndex, sortedRecords]);

  useEffect(() => {
    const companyId = workforce?.companyId;
    const workforceUid = workforce?.uid;
    if (!socket || !workforceUid || !companyId) return;
    const room = `room_company_${companyId}_workforce_${workforceUid}`;
    const join = () => socket.emit("join", [room]);
    if (socket.connected) join();
    socket.on("connect", join);

    const handler = (data) => {
      // Handle both JSON string and object formats
      const dataParsed = typeof data === 'string' ? JSON.parse(data) : data;

      //const isSameUser = dataParsed.userUid && workforce?.uid ? dataParsed.userUid === workforce.uid : true;
      //const isSameDevice =
      //  dataParsed.uid && workforce?.deviceTracker?.physicalDeviceUid ? dataParsed.uid === workforce.deviceTracker.physicalDeviceUid : true;

      //if (!isSameUser || !isSameDevice) return;

      if (dataParsed.kind === "position") {
        const lat = parseFloat(dataParsed.gps?.latitude);
        const lng = parseFloat(dataParsed.gps?.longitude);
        if (isNaN(lat) || isNaN(lng)) return;
        setUser({ lat, lng });
        return;
      }

      // Validate createdAt before processing (may not be present in payload, use current time as fallback)
      if (!dataParsed.createdAt) {
        dataParsed.createdAt = new Date().toISOString();
      }

      // Validate that createdAt is a valid date
      const createdAtDate = dayjs(dataParsed.createdAt);
      if (!createdAtDate.isValid()) {
        console.warn("[WorkforceMap] Received record with invalid createdAt:", dataParsed.createdAt);
        return;
      }

      const isSameDayAsSelected = createdAtDate.isSame(selectedDate, "day");
      if (!isSameDayAsSelected) return;

      addRecord(dataParsed);
    };

    socket.on("deviceTrackerMessage", handler);
    return () => {
      socket.off("connect", join);
      socket.off("deviceTrackerMessage", handler);
    };
  }, [socket, workforce?.uid, workforce?.companyId, selectedDate, addRecord]);

  const employeeLocation = mapFocus;

  useEffect(() => {
    if (autoFollow && mapRef.current && employeeLocation) {
      mapRef.current.panTo(employeeLocation);
    }
  }, [employeeLocation?.lat, employeeLocation?.lng, autoFollow]);

  const handleMapLoad = (map) => {
    mapRef.current = map;

    if (mapFocus) {
      setMapCenter(mapFocus);
      map.panTo(mapFocus);
      map.setZoom(16);
    }

    setTimeout(() => {
      if (window.google && window.google.maps) {
        window.google.maps.event.trigger(map, "resize");
      }
    }, 500);
  };

  const historicalMarkers = useMemo(() => {
    if (!Array.isArray(sortedRecords) || sortedRecords.length === 0) return null;

    const MAX_MARKERS = 350;
    const step = Math.ceil(sortedRecords.length / MAX_MARKERS);

    return sortedRecords
      .filter((_, idx) => idx % step === 0)
      .map((record, index) => {
        const lat = parseFloat(record?.gps?.latitude);
        const lng = parseFloat(record?.gps?.longitude);
        if (isNaN(lat) || isNaN(lng)) return null;

        const handleClick = () => {
          setAutoFollow(false);
          if (mapRef.current) {
            mapRef.current.panTo({ lat, lng });
          }
          setInfoRecord(record);
          setRecordIndex(index * step);
        };

        const isSelected = recordIndex === index * step;

        return (
          <MarkerF
            key={`${record.uid}`}
            position={{ lat, lng }}
            onClick={handleClick}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: isSelected ? 10 : 6,
              fillColor: isSelected ? colors.orange.base : colors.blue.base,
              fillOpacity: isSelected ? 1 : 0.85,
              strokeColor: colors.warmWhite.base,
              strokeWeight: isSelected ? 2.5 : 1.5,
            }}
          />
        );
      });
  }, [sortedRecords, recordIndex]);

  const findRecordIndexBySeconds = (seconds) => {
    if (!Array.isArray(sortedRecords) || sortedRecords.length === 0) return -1;
    let minDiff = Infinity;
    let closestIdx = -1;
    sortedRecords.forEach((rec, idx) => {
      if (!rec?.createdAt) return;
      const date = new Date(rec.createdAt);
      if (isNaN(date.getTime())) return;
      const sec = date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
      const diff = Math.abs(sec - seconds);
      if (diff < minDiff) {
        minDiff = diff;
        closestIdx = idx;
      }
    });
    return closestIdx;
  };

  const handleCursorChange = (seconds) => {
    const idx = findRecordIndexBySeconds(seconds);
    if (idx !== -1) {
      setRecordIndex(idx);
    }
  };

  const handleTimelineDragStart = () => {
    setAutoFollow(false);
  };

  // Helper function to find the last valid record with GPS data
  const getLastValidRecord = () => {
    if (!Array.isArray(sortedRecords) || sortedRecords.length === 0) return null;

    // Search from the end to find the last record with valid GPS coordinates
    for (let i = sortedRecords.length - 1; i >= 0; i--) {
      const record = sortedRecords[i];
      if (!record?.gps) continue;

      const lat = parseFloat(record.gps.latitude);
      const lng = parseFloat(record.gps.longitude);

      // Validate coordinates: not NaN, not null, not undefined, and not 0,0 (which is a default/invalid location)
      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0 && lat !== null && lng !== null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { record, index: i, lat, lng };
      }
    }
    return null;
  };

  const handleGoToLastPosition = () => {
    setInfoRecord(null);
    const lastValid = getLastValidRecord();
    if (lastValid && mapRef.current) {
      // Validate coordinates one more time before moving
      const lat = parseFloat(lastValid.record?.gps?.latitude);
      const lng = parseFloat(lastValid.record?.gps?.longitude);

      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        // Update state first
        setRecordIndex(lastValid.index);
        setAutoFollow(true);

        // Move map directly to ensure it goes to the correct position
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.panTo({ lat, lng });
            mapRef.current.setZoom(19);

            // Trigger resize to ensure map updates correctly
            if (window.google && window.google.maps) {
              window.google.maps.event.trigger(mapRef.current, "resize");
            }
          }
        }, 50);
      }
    }
  };

  return (
    <Box
      sx={{
        height: mapHeight,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        mb: 2,
        p: 2,
      }}
    >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", position: "relative" }}>
        {/* Mapa */}
        {!mapFocus ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 280,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              borderRadius: "12px",
              bgcolor: colors.gray[50],
            }}
          >
            <CircularProgress size={32} sx={{ color: "#f97316" }} />
            <Typography sx={{ fontSize: "0.9rem", color: colors.gray[500] }}>Looking for the current location...</Typography>
          </Box>
        ) : loading || !isMapReady ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={mapHeight}
            sx={{
              borderRadius: "12px",
              backgroundColor: colors.gray[50],
            }}
          />
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapFocus}
            zoom={sortedRecords.length > 0 ? 15 : 12}
            options={{
              fullscreenControl: false,
              streetViewControl: false,
              mapTypeControl: false,
              zoomControl: true,
              styles: [
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
              ],
            }}
            onLoad={handleMapLoad}
          >
            {user && (
              <Marker
                position={user}
                onClick={() => {
                  if (sortedRecords.length > 0) {
                    setInfoRecord(sortedRecords[sortedRecords.length - 1]);
                  }
                }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 12,
                  fillColor: colors.danger.base,
                  fillOpacity: 1,
                  strokeColor: colors.warmWhite.base,
                  strokeWeight: 3,
                }}
              />
            )}

            {historicalMarkers}

            {pathCoords.length >= 2 && (
              <Polyline
                path={pathCoords}
                options={{
                  strokeColor: colors.blue.base,
                  strokeOpacity: 0.7,
                  strokeWeight: 3,
                  icons: [
                    {
                      icon: {
                        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 2,
                        strokeColor: colors.blue[700],
                        strokeWeight: 2,
                        fillColor: colors.blue[700],
                        fillOpacity: 1,
                      },
                      offset: "0",
                      repeat: "60px",
                    },
                  ],
                }}
              />
            )}

            {infoRecord && (
              <InfoWindow
                position={{
                  lat: parseFloat(infoRecord.gps.latitude),
                  lng: parseFloat(infoRecord.gps.longitude),
                }}
                onCloseClick={() => setInfoRecord(null)}
              >
                <RecordInfo record={infoRecord} />
              </InfoWindow>
            )}
          </GoogleMap>
        )}

        {/* Controles */}
        <Box width="100%" display="flex" justifyContent="center" sx={{ mt: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{
              px: 2,
              py: 1.2,
            }}
          >
            <DateSelector
              value={selectedDate}
              onChange={setSelectedDate}
              sx={{
                backgroundColor: "transparent",
                borderRadius: 0,
                border: "none",
                boxShadow: "none",
                p: 0,
              }}
            />

            <Box
              sx={{
                width: "1px",
                height: 32,
                backgroundColor: colors.gray[200],
              }}
            />

            <Tooltip>
              <Box>
                <Button
                  disabled={loading || trackerRecords.length === 0}
                  onClick={handleGoToLastPosition}
                  startIcon={<GpsFixedIcon fontSize="small" />}
                  disableRipple
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    px: 1.8,
                    py: 0.5,
                    fontSize: 13,
                    border: `1px solid ${autoFollow ? colors.orange[300] : colors.gray[200]}`,
                    backgroundColor: colors.gray[50],
                    color: autoFollow ? colors.orange.base : colors.gray[600],
                    opacity: autoFollow ? 1 : 0.85,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: colors.gray[100],
                      borderColor: autoFollow ? colors.orange[400] : colors.gray[300],
                      color: autoFollow ? colors.orange[700] : colors.gray[700],
                    },
                    "&.Mui-disabled": {
                      backgroundColor: colors.gray[50],
                      borderColor: colors.gray[200],
                      color: colors.gray[300],
                      opacity: 1,
                    },
                    "&.Mui-focusVisible": {
                      outline: "none",
                      boxShadow: `0 0 0 2px ${colors.gray[200]}`,
                    },
                  }}
                >
                  Last location
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </Box>

        <IconButtonComponent
          disabled={loading || trackerRecords.length === 0}
          theme={autoFollow ? "color" : "grey"}
          sx={{
            opacity: autoFollow ? 1 : 0.6,
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 1000,
            width: "auto",
            height: "auto",
          }}
          sxIcon={{
            color: autoFollow ? undefined : colors.gray[600],
          }}
          onClick={handleGoToLastPosition}
        >
          <Tooltip title="Follow last position">
            <GpsFixedIcon />
          </Tooltip>
        </IconButtonComponent>
      </Box>

      {/* Timeline pegada abajo */}
      <TimeLine
        events={sortedRecords}
        barHeight={64}
        loading={loading}
        followLast={autoFollow}
        onCursorChange={handleCursorChange}
        onDragStart={handleTimelineDragStart}
        sx={{ mt: "auto" }}
      />
    </Box>
  );
};
